'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ComponentProps } from 'react'

import {
  type RumProps,
  useTrackRumAction,
} from '../../providers/real-user-monitoring'
import { useProgressBar } from './progress-bar'

/**
 * A wrapper around next/link that provides default focus styles and will trigger a progress bar when navigating.
 *
 * External links (including mailto and download links) will open in a new tab by default. To prevent this provide `openNewTab={false}`.
 *
 * To suppress the progress bar, pass `hideProgressBar` prop.
 */
export function BaseAnchor({
  className,
  onClick,
  hideProgressBar,
  openNewTab,
  rumActionName,
  rumData,
  prefetch = false,
  ...props
}: ComponentProps<typeof Link> & {
  /** Do not show the progress bar when navigating */
  hideProgressBar?: boolean
  /** Open this link in a new tab */
  openNewTab?: boolean
  /** Initiates a download if the href is a file. If this prop is not provided,
   * downloading will not work in Vercel deployed environments and you will get a 404 page not found.
   */
  download?: boolean
  /** We disable prefetching by default while we investigate issues with a lot of prefetch errors
   * in datadog https://app.datadoghq.eu/logs?query=issue.id%3A8fd84136-a664-11f0-80d0-da7ad0900005%20%40dd.monitor.group%3Afrontend_anonymous%20env%3Aprod%20%22The%20router%20state%20header%20was%20sent%20%22&agg_m=count&agg_m_source=base&agg_t=count&cols=host%2Cservice&event=AwAAAZtmBD0vavqlRAAAABhBWnRtQkZWYUFBQUFYYlU3Q0h1OU5BQUIAAAAkMTE5YjY2OGItZDllZi00YzNjLWI1YjUtY2U5ZmVkZjUxOTdmAAAe8w&fromUser=true&messageDisplay=inline&refresh_mode=sliding&storage=hot&stream_sort=desc&viz=stream&from_ts=1765788572526&to_ts=1766998172526&live=false,
   *  you can override this by passing `prefetch={true}` */
  prefetch?: boolean
} & RumProps) {
  const stringHref = String(props.href)
  const isExternal = stringHref.startsWith('http')
  const isMailto = stringHref.startsWith('mailto:')
  const isDownload = Boolean(props.download)

  const progressBar = useProgressBar()
  const router = useRouter()

  // By default we'll open external links in a new tab
  const defaultShouldOpenInNewTab = isExternal || isMailto || isDownload
  // But if the user provides a value for `openNewTab`, we'll use that instead
  const shouldOpenInNewTab = openNewTab ?? defaultShouldOpenInNewTab
  const openInNewTabProps = shouldOpenInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {}

  const trackRumAction = useTrackRumAction()

  return (
    <Link
      onClick={(event) => {
        onClick?.(event)
        if (rumActionName) {
          trackRumAction(rumActionName, 'click', rumData)
        }

        const isNewTabClick =
          event.metaKey || // Command (Mac) or Control (Windows)
          event.ctrlKey || // Control
          event.shiftKey // Shift

        const shouldHideProgressBar =
          isNewTabClick ||
          hideProgressBar ||
          shouldOpenInNewTab ||
          isExternal ||
          isMailto ||
          isDownload

        if (shouldHideProgressBar) {
          return
        }

        event.preventDefault()
        progressBar.startTransition(() => {
          const navigateOptions = {
            scroll: props.scroll,
          }
          if (props.replace) {
            router.replace(stringHref, navigateOptions)
          } else {
            router.push(stringHref, navigateOptions)
          }
        })
      }}
      className={className}
      prefetch={prefetch}
      {...openInNewTabProps}
      {...props}
    />
  )
}
